import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ToggleLeft, ToggleRight } from 'lucide-react';
import { roleService, RoleDefinition } from '../../../../components/api/roleService';
import { useAccessTheme } from './useAccessTheme';

interface Props {
  onClose: () => void;
  onSubmit: () => void;
  role?: RoleDefinition | null;
}

const AddRoleModal: React.FC<Props> = ({ onClose, onSubmit, role }) => {
  const t = useAccessTheme();
  const isEditMode = !!role?.id;
  const [form, setForm] = useState({
    roleName: '',
    description: '',
    isAdmin: false,
    isActive: true,
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEditMode && role) {
      setForm({
        roleName: role.roleName || '',
        description: role.description || '',
        isAdmin: !!role.isAdmin,
        isActive: role.isActive !== false,
      });
      return;
    }
    setForm({
      roleName: '',
      description: '',
      isAdmin: false,
      isActive: true,
    });
  }, [role, isEditMode]);

  const handleChange = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.roleName.trim()) {
      setError('Role name is required.');
      return;
    }
    if (isEditMode && !role?.id) {
      setError('Missing role id for update.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (isEditMode) {
        await roleService.updateRole({
          id: role!.id,
          roleName: form.roleName.trim(),
          description: form.description.trim(),
          isAdmin: form.isAdmin,
          isActive: form.isActive,
        });
      } else {
        await roleService.createRole({
          roleName: form.roleName.trim(),
          description: form.description.trim(),
          isAdmin: form.isAdmin,
          isActive: form.isActive,
        });
      }
      onSubmit();
    } catch (e) {
      console.error(e);
      setError(isEditMode ? 'Failed to update role. Please try again.' : 'Failed to create role. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const labelCls = 'block text-xs font-medium mb-1 ' + t.muted;
  const inputCls = 'w-full border rounded-lg px-3 py-2 text-sm outline-none ' + t.input;

  return createPortal(
    <div
      className={'fixed inset-0 flex items-center justify-center p-4 ' + t.overlay}
      style={{ zIndex: 200 }}
    >
      <div className={'rounded-xl shadow-xl w-full max-w-md overflow-hidden border ' + t.panel}>
        <div className={'flex items-center justify-between px-5 py-4 border-b ' + t.header}>
          <h3 className={'text-base font-semibold ' + t.title}>
            {isEditMode ? 'Edit User Role' : 'Create User Role'}
          </h3>
          <button type="button" onClick={onClose} className={'p-1.5 rounded-lg ' + t.closeBtn}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {isEditMode && role?.id ? (
            <div>
              <label className={labelCls}>Id</label>
              <input
                type="text"
                value={role.id}
                disabled
                className={inputCls + ' opacity-60'}
                readOnly
              />
            </div>
          ) : null}

          <div>
            <label className={labelCls}>Role Name *</label>
            <input
              type="text"
              value={form.roleName}
              onChange={(e) => handleChange('roleName', e.target.value)}
              className={inputCls}
              placeholder="e.g. Selfic User"
            />
          </div>

          <div>
            <label className={labelCls}>Description</label>
            <textarea
              value={form.description}
              rows={3}
              onChange={(e) => handleChange('description', e.target.value)}
              className={inputCls + ' resize-none'}
              placeholder="Describe this role"
            />
          </div>

          <div className={'flex items-center justify-between px-3 py-2.5 rounded-lg border ' + t.border}>
            <div>
              <div className={'text-sm font-medium ' + t.title}>Is Admin</div>
              <div className={'text-xs ' + t.muted}>Grant administrative privileges</div>
            </div>
            <button type="button" onClick={() => handleChange('isAdmin', !form.isAdmin)}>
              {form.isAdmin
                ? <ToggleRight className={'w-8 h-8 ' + t.toggleOn} />
                : <ToggleLeft className={'w-8 h-8 ' + t.toggleOff} />}
            </button>
          </div>

          <div className={'flex items-center justify-between px-3 py-2.5 rounded-lg border ' + t.border}>
            <div>
              <div className={'text-sm font-medium ' + t.title}>Is Active</div>
              <div className={'text-xs ' + t.muted}>Role can be assigned to users</div>
            </div>
            <button type="button" onClick={() => handleChange('isActive', !form.isActive)}>
              {form.isActive
                ? <ToggleRight className={'w-8 h-8 ' + t.toggleOn} />
                : <ToggleLeft className={'w-8 h-8 ' + t.toggleOff} />}
            </button>
          </div>

          {error ? (
            <div className={'border rounded-lg px-3 py-2 text-xs ' + t.errorBox}>{error}</div>
          ) : null}
        </div>

        <div className={'px-5 py-4 border-t flex justify-end gap-2 ' + t.footer}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={'px-4 py-2 rounded-lg text-sm font-medium ' + t.btnGhost}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className={'px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ' + t.btnPrimary}
          >
            {saving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Role'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddRoleModal;
